// UI feedback only. Account identity, link validity and permissions stay on the server.
// Scoped sv/en copy keeps this package compatible with the existing locale files.
const messages = {
  checkInbox: ['Kontrollera din mejl', 'Check your email'],
  requestedFor: ['Din begäran gäller:', 'Your request is for:'],
  magicRequested: [
    'Om inloggning är tillgänglig för uppgiften skickas en länk till kontots registrerade mejladress. För ett användarnamnskonto kan det vara vårdnadshavarens adress.',
    'If sign-in is available for this identifier, a link is sent to the account’s registered email address. For a username account, this may be a guardian’s address.',
  ],
  resetRequested: [
    'Om kontot kan återställas skickas en länk till dess registrerade mejladress. För ett användarnamnskonto kan det vara vårdnadshavarens adress.',
    'If the account can be recovered, a link is sent to its registered email address. For a username account, this may be a guardian’s address.',
  ],
  editRequest: ['Ändra uppgift eller begär en ny länk', 'Edit identifier or request a new link'],
  passwordLogin: ['Logga in med lösenord', 'Sign in with password'],
  requestMagic: ['Begär en ny inloggningslänk', 'Request a new sign-in link'],
  requestReset: ['Begär en ny återställningslänk', 'Request a new reset link'],
  retry: ['Försök igen', 'Try again'],
  network: ['Ingen kontakt med servern. Kontrollera anslutningen och försök igen.', 'Cannot reach the server. Check your connection and try again.'],
  rateLimit: ['För många försök just nu. Vänta en stund och försök igen.', 'Too many attempts. Please wait a little and try again.'],
  service: ['Tjänsten kunde inte slutföra begäran. Försök igen om en stund.', 'The service could not complete the request. Please try again shortly.'],
  credentials: ['E-post, användarnamn eller lösenord stämmer inte. Försök igen eller återställ lösenordet.', 'The email, username or password is incorrect. Try again or reset your password.'],
  invalidMagic: ['Länken är ogiltig, har gått ut eller har redan använts. Begär en ny inloggningslänk.', 'The link is invalid, expired or already used. Request a new sign-in link.'],
  invalidReset: ['Länken kan inte användas längre. Begär en ny återställningslänk.', 'This link can no longer be used. Request a new reset link.'],
  fields: ['Kontrollera uppgifterna och försök igen.', 'Check the details and try again.'],
  requestFailed: ['Begäran kunde inte slutföras. Försök igen.', 'The request could not be completed. Please try again.'],
  sessionChanged: ['Inloggningen kunde inte slutföras i den här fliken. Logga in igen.', 'Sign-in could not be completed in this tab. Please sign in again.'],
  resetUnconfirmed: ['Ändringen kunde inte bekräftas. Prova det nya lösenordet vid inloggning eller begär en ny återställningslänk.', 'The change could not be confirmed. Try signing in with the new password or request a new reset link.'],
  keepAccount: ['Fortsätt med ditt inloggade konto', 'Continue with your signed-in account'],
  switchAccount: ['Logga ut och logga in med det ändrade kontot', 'Sign out and sign in with the updated account'],
};

export function authFlowMessage(i18n, key) {
  const english = String(i18n?.resolvedLanguage || i18n?.language || 'sv').toLowerCase().split('-')[0] === 'en';
  return (messages[key] || messages.requestFailed)[english ? 1 : 0];
}

export function isTemporaryAuthError(error) {
  const status = error?.response?.status;
  return !status || status === 408 || status === 429 || status >= 500;
}

export function authErrorMessage(error, i18n, operation) {
  const status = error?.response?.status;
  // Never render arbitrary FastAPI detail objects/arrays as React children.
  if (!status || status === 408) return authFlowMessage(i18n, 'network');
  if (status === 429) return authFlowMessage(i18n, 'rateLimit');
  if (status >= 500) return authFlowMessage(i18n, 'service');
  if (operation === 'login' && status === 401) return authFlowMessage(i18n, 'credentials');
  if ([400, 401, 403, 404, 410].includes(status) && ['magic', 'reset'].includes(operation)) {
    return authFlowMessage(i18n, operation === 'magic' ? 'invalidMagic' : 'invalidReset');
  }
  return authFlowMessage(i18n, [400, 422].includes(status) ? 'fields' : 'requestFailed');
}

export function initialLoginMode(location) {
  const queryMode = new URLSearchParams(location?.search || '').get('mode');
  const mode = queryMode || location?.state?.mode;
  return mode === 'password' ? 'password' : 'magic';
}

export function safeAuthNext(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(decoded)) return null;
    const url = new URL(raw, 'https://internal.invalid');
    return url.origin === 'https://internal.invalid' ? raw : null;
  } catch { return null; }
}

export function resetLoginIdentifier(target) {
  // Contact email is delivery-only and must never select a parent/sibling account.
  const value = target?.login_mode === 'username'
    ? target?.username : (target?.email || target?.username);
  return typeof value === 'string' ? value : '';
}
